import React, { Suspense, lazy } from 'react';
import ReactDOM from 'react-dom';
import { Routes, Route, Outlet } from 'react-router-dom';
import { SignIn, SignUp, ConfirmSignUp, ForgotPassword, ProtectedRoute, Setup } from './ui/components/Login';
import APIService from './services/APIService';
import { getUrlParam, setPageTitle } from './utils/Utils';
import './fontAwesomeSetup';
import TwitterCallback from './ui/components/TwitterLogin/TwitterCallback';
import YoutubeCallback from './ui/components/YoutubeLogin/YoutubeCallback';
import './App.scss';

const MainLayout = lazy(() => import(/* webpackChunkName: "MainLayout" */ './ui/layouts/main/MainLayout'));
const InfluencerFormContainer = lazy(
  () => import(/* webpackChunkName: "InfluencerFormContainer" */ './ui/containers/InfluencerFormContainer'),
);
const AdminDashboard = lazy(
  () => import(/* webpackChunkName: "AdminDashboardContainer" */ './ui/views/admin/AdminDashboard'),
);

// const CompetitionAnalyticsView = lazy(
//   () => import(/* webpackChunkName: "CompetitionAnalyticsVieqw" */ './ui/views/analytics/competitionAnalytics'),
// );

const GiggConfirmSignUp = lazy(
  () => import(/* webpackChunkName: "GiggConfirmSignUp" */ './ui/components/GiggAuth/GiggConfirmSignUp'),
);
const GiggResetPassword = lazy(
  () => import(/* webpackChunkName: "GiggResetPassword" */ './ui/components/GiggAuth/GiggResetPassword'),
);
const OAuth = lazy(() => import(/* webpackChunkName: "OAuth" */ './ui/components/GiggAuth/OAuth'));
const IdeaChecker = lazy(() => import(/* webpackChunkName: "IdeaChecker" */ './ui/components/IdeaChecker'));

const MyEntitiesContainer = lazy(
  () => import(/* webpackChunkName: "MyEntitiesContainer" */ './ui/containers/MyEntitiesContainer'),
);
const EntityHome = lazy(() => import(/* webpackChunkName: "EntityHome" */ './ui/views/EntityHome'));
const Board = lazy(() => import(/* webpackChunkName: "Board" */ './ui/views/inbox/Board'));
const EntitySettings = lazy(() => import(/* webpackChunkName: "InboxSettings" */ './ui/views/EntitySettings'));
const EntityAnalytics = lazy(() => import(/* webpackChunkName: "BoardAnalyticsView" */ './ui/views/EntityAnalytics'));
const RoundsBreakdown = lazy(() => import(/* webpackChunkName: "InboxSettings" */ './ui/views/competition/RoundsBreakdown'));
const Settings = lazy(() => import(/* webpackChunkName: "Settings" */ './ui/views/settings/Settings'));
const AdminOrgPageContainer = React.lazy(
  () => import(/* webpackChunkName: "AdminOrgPageContainer" */ './ui/containers/Admin/AdminOrgPageContainer'),
);
const AdminOrgsListContainer = React.lazy(
  () => import(/* webpackChunkName: "AdminOrgsListContainer" */ './ui/containers/Admin/AdminOrgsListContainer'),
);

const AdminBoardsSummaryContainer = React.lazy(
  () => import(/* webpackChunkName: "AdminBoardsSummaryContainer" */ './ui/containers/Admin/AdminBoardsSummaryContainer'),
);

const AdminReportsContainer = React.lazy(
  () => import(/* webpackChunkName: "AdminReportsContainer" */ './ui/containers/Admin/AdminReportsContainer'),
);

const AdminLogsView = React.lazy(() => import(/* webpackChunkName: "AdminLogsView" */ './ui/views/admin/AdminLogsView'));

const AdminSystemSummary = React.lazy(
  () => import(/* webpackChunkName: "AdminSystemSummary" */ './ui/views/admin/AdminSystemSummary'),
);

export type OrgParams = {
  orgPath: string;
  entityType: string;
  entityId: string;
  boardId: string;
};

export interface AppProps {
  showRefreshButton: () => void;
  doShowRefreshButton: boolean;
}

class App extends React.Component<AppProps, {}> {
  componentDidMount() {
    setPageTitle();
  }
  toggleRefresh = () => {
    this.props.showRefreshButton();
  };

  renderRefreshBtn = () => {
    const boardId = getUrlParam(window.location.pathname, '/:orgPath/board/:boardId', 'boardId');
    if (boardId) {
      return ReactDOM.createPortal(
        <button
          className="ant-btn"
          style={{ position: 'fixed', left: '10px', bottom: '10px' }}
          onClick={() =>
            APIService.POST(`/board/${boardId}/refreshkeywords`, undefined).then(() => window.location.reload())
          }
        >
          Refresh
        </button>,
        document.body,
      );
    }

    return null;
  };

  public render() {
    return (
      <>
        <div className="App">
          <Suspense fallback={<></>}>
            <Routes>
              <Route path="/login" element={<SignIn />} />
              <Route path="/register" element={<SignUp />} />
              <Route path="/forgot" element={<ForgotPassword />} />
              <Route path="/public">
                <Route path="verification" element={<GiggConfirmSignUp />} />
                <Route path="reset" element={<GiggResetPassword />} />
                <Route path="oauth-redirect" element={<OAuth />} />
                <Route path="idea" element={<IdeaChecker />} />
              </Route>
              <Route path="/verification" element={<ConfirmSignUp />} />
              <Route path="/influencer" element={<InfluencerFormContainer />} />
              <Route path="/setup" element={<Setup />} />
              <Route path="/twitter-callback" element={<TwitterCallback />} />
              <Route path="/youtube-callback" element={<YoutubeCallback />} />
              <Route element={<ProtectedRoute />}>
                <Route path="/admin" element={<AdminDashboard />}>
                  <Route index element={<AdminSystemSummary />} />
                  <Route path="orgs/:orgId" element={<AdminOrgPageContainer />} />
                  <Route path="orgs" element={<AdminOrgsListContainer />} />
                  <Route path="boards" element={<AdminBoardsSummaryContainer />} />
                  <Route path="reports" element={<AdminReportsContainer />} />
                  <Route path="logs" element={<AdminLogsView />} />
                </Route>
              </Route>
              <Route element={<ProtectedRoute />}>
                <Route path="/" element={<MainLayout />}>
                  <Route path="/:orgPath/:entityType?" element={<Outlet />}>
                    <Route index element={<MyEntitiesContainer />} />
                    <Route path="settings" element={<Settings />} />
                    <Route path=":entityId" element={<EntityHome />} />
                    <Route path=":entityId/board/:boardId" element={<Board />} />
                    <Route path=":entityId/analytics/:tab" element={<EntityAnalytics />} />
                    <Route path=":entityId/settings">
                      <Route index element={<EntitySettings />} />
                      <Route path=":tab/*" element={<EntitySettings />} />
                    </Route>
                    <Route path=":entityId/submissions">
                      <Route index element={<RoundsBreakdown />} />
                      <Route path=":stage/*" element={<RoundsBreakdown />} />
                    </Route>
                  </Route>
                </Route>
              </Route>
            </Routes>
          </Suspense>
        </div>
      </>
    );
  }
}

export default App;
